import {
  ChatAlt2Icon,
  HomeIcon,
  MenuIcon,
  PhotographIcon,
  ViewGridIcon,
} from '@heroicons/react/outline';
import {
  ChatAlt2Icon as ChatAlt2IconSolid,
  HomeIcon as HomeIconSolid,
  PhotographIcon as PhotographIconSolid,
  ViewGridIcon as ViewGridIconSolid,
} from '@heroicons/react/solid';
import Link from 'next/link';
import { useRouter } from 'next/router';
import useLocale from 'next-translate/useTranslation';
import * as React from 'react';

import clsxm from '@/lib/clsxm';

import { useAnalytics } from '@/contexts/Analytics';

type Icon = React.ComponentType<React.ComponentProps<'svg'>>;

/**
 * The phone tab bar's destinations, picked from where phone users go (PostHog,
 * Oct 2026): AI Images and Marky lead, then All Tools, the way into the 200+
 * tools (Dmytro's call, 2026-10-23; it replaced History, which is at the top
 * of Menu). Everything else is one tap away in Menu (the sidebar) or on Home.
 */
const TABS: Array<{
  id: string;
  /** A key of the `dashboard` namespace, except brand names. */
  label: string;
  translate: boolean;
  href: string;
  icon: Icon;
  activeIcon: Icon;
}> = [
  {
    id: 'home',
    label: 'Home',
    translate: true,
    href: '/dashboard',
    icon: HomeIcon,
    activeIcon: HomeIconSolid,
  },
  {
    id: 'marky',
    label: 'Marky',
    translate: false,
    href: '/chat',
    icon: ChatAlt2Icon,
    activeIcon: ChatAlt2IconSolid,
  },
  {
    id: 'images',
    label: 'Images',
    translate: true,
    href: '/ai-images',
    icon: PhotographIcon,
    activeIcon: PhotographIconSolid,
  },
  {
    id: 'tools',
    // "All Tools": the sidebar's label for /presets.
    label: 'Tools',
    translate: true,
    href: '/presets',
    icon: ViewGridIcon,
    activeIcon: ViewGridIconSolid,
  },
];

// `router.pathname` is the route pattern, without the locale prefix.
const isActive = (pathname: string, href: string) =>
  pathname === href ||
  (href !== '/dashboard' && pathname.startsWith(`${href}/`));

const NON_TEXT_INPUTS = new Set([
  'button',
  'checkbox',
  'color',
  'file',
  'hidden',
  'image',
  'radio',
  'range',
  'reset',
  'submit',
]);

const isTextField = (element: Element | null) => {
  if (!(element instanceof HTMLElement)) return false;
  if (element.isContentEditable) return true;
  if (element instanceof HTMLTextAreaElement) {
    return !element.readOnly && !element.disabled;
  }
  if (element instanceof HTMLInputElement) {
    return (
      !element.readOnly &&
      !element.disabled &&
      !NON_TEXT_INPUTS.has(element.type)
    );
  }
  return false;
};

/**
 * True while the on-screen keyboard is up. A bar fixed to the bottom would
 * otherwise ride on top of the keyboard (Android) or float mid-screen when the
 * page scrolls under it (iOS Safari). A focused field alone is not enough:
 * pages that autofocus a field on load have no keyboard showing, so the
 * visual viewport must also have shrunk.
 */
function useOnScreenKeyboard() {
  const [open, setOpen] = React.useState(false);

  React.useEffect(() => {
    if (!window.matchMedia?.('(pointer: coarse)').matches) return;
    const viewport = window.visualViewport;
    let frame = 0;
    const update = () => {
      frame = 0;
      const typing = isTextField(document.activeElement);
      setOpen(
        typing &&
          (!viewport ||
            (viewport.scale < 1.05 &&
              window.innerHeight - viewport.height > 120)),
      );
    };
    // Focus moving between two fields fires focusout before focusin; one
    // frame later both have happened, so the bar doesn't flash in between.
    const schedule = () => {
      if (!frame) frame = window.requestAnimationFrame(update);
    };
    document.addEventListener('focusin', schedule);
    document.addEventListener('focusout', schedule);
    viewport?.addEventListener('resize', schedule);
    return () => {
      document.removeEventListener('focusin', schedule);
      document.removeEventListener('focusout', schedule);
      viewport?.removeEventListener('resize', schedule);
      if (frame) window.cancelAnimationFrame(frame);
    };
  }, []);

  return open;
}

// Bars on screen. While any is, `--mobile-tab-bar-height` (globals.css) lifts
// other bottom-fixed UI above it: audio players, the media selection bar,
// floating buttons, the cookie banner and toasts.
let mountedBars = 0;

/**
 * Bottom tab bar for phones (below `md`), rendered by DashboardLayout and,
 * where a page opts in, NarrowDashboardLayout. Render `MobileTabBarSpacer` as
 * the last child of the layout's column so the page's own scroll container
 * ends above the bar.
 */
export default function MobileTabBar({
  onMenu,
  menuOpen,
}: {
  /** Opens the layout's sidebar dialog. */
  onMenu: () => void;
  menuOpen: boolean;
}) {
  const { t } = useLocale('dashboard');
  const { pathname } = useRouter();
  const analytics = useAnalytics();
  const keyboardOpen = useOnScreenKeyboard();

  React.useEffect(() => {
    const root = document.documentElement;
    mountedBars += 1;
    root.setAttribute('data-mobile-tab-bar', '');
    return () => {
      mountedBars = Math.max(0, mountedBars - 1);
      if (!mountedBars) root.removeAttribute('data-mobile-tab-bar');
    };
  }, []);

  // Autocapture is off in PostHog, and the sidebar links to the same pages,
  // so the bar's own taps are tracked explicitly.
  const track = (tab: string) =>
    analytics.track('Mobile Tab Bar Click', { tab, from: pathname });

  const itemClass =
    'group flex min-w-0 flex-col items-center justify-center gap-0.5 rounded-xl px-0.5 text-[11px] font-medium leading-4 transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-primary-600';
  const iconWrapClass =
    'flex h-7 w-12 items-center justify-center rounded-full transition-colors duration-200';

  return (
    <nav
      className={clsxm(
        // The top hairline is an inset shadow, not a border, so the bar is
        // exactly as tall as MobileTabBarSpacer.
        'fixed inset-x-0 bottom-0 z-30 bg-white/90 pb-[env(safe-area-inset-bottom)] shadow-[inset_0_1px_0_rgb(15_15_40/0.08)] backdrop-blur-xl dark:bg-slate-900/90 dark:shadow-[inset_0_1px_0_rgb(255_255_255/0.08)] md:hidden print:hidden',
        keyboardOpen && 'hidden',
      )}
    >
      <div className='mx-auto grid h-14 max-w-lg grid-cols-5 px-1'>
        {TABS.map((tab) => {
          const active = isActive(pathname, tab.href);
          const TabIcon = active ? tab.activeIcon : tab.icon;
          return (
            <Link
              key={tab.id}
              href={tab.href}
              aria-current={active ? 'page' : undefined}
              onClick={() => track(tab.id)}
              className={clsxm(
                itemClass,
                active
                  ? 'text-primary-600 dark:text-primary-300'
                  : 'text-[#6f6f86] active:text-gray-900 dark:text-slate-400 dark:active:text-slate-100',
              )}
            >
              <span
                className={clsxm(
                  iconWrapClass,
                  active
                    ? 'bg-primary-50 dark:bg-primary-500/15'
                    : 'group-active:bg-gray-100 dark:group-active:bg-white/[0.06]',
                )}
              >
                <TabIcon className='h-[22px] w-[22px]' aria-hidden='true' />
              </span>
              <span className='max-w-full truncate'>
                {tab.translate ? t(tab.label) : tab.label}
              </span>
            </Link>
          );
        })}
        <button
          type='button'
          aria-haspopup='dialog'
          aria-expanded={menuOpen}
          onClick={() => {
            track('menu');
            onMenu();
          }}
          className={clsxm(
            itemClass,
            'text-[#6f6f86] active:text-gray-900 dark:text-slate-400 dark:active:text-slate-100',
          )}
        >
          <span
            className={clsxm(
              iconWrapClass,
              'group-active:bg-gray-100 dark:group-active:bg-white/[0.06]',
            )}
          >
            <MenuIcon className='h-[22px] w-[22px]' aria-hidden='true' />
          </span>
          <span className='max-w-full truncate'>{t('Menu')}</span>
        </button>
      </div>
    </nav>
  );
}

/**
 * Takes the bar's height at the end of the layout's flex column: a page that
 * scrolls inside its own `flex-1` main gets a shorter main, and a page that
 * scrolls the document gets room after its last line.
 */
export function MobileTabBarSpacer() {
  return (
    <div
      aria-hidden='true'
      className='h-[calc(3.5rem+env(safe-area-inset-bottom))] shrink-0 md:hidden print:hidden'
    />
  );
}
